import { Injectable } from '@angular/core';
import { FlamingoAdapter } from '@mode-switch/adapter-flamingo';
import { DataRole, DataType } from '@mode-switch/express';
import { Attr, Sort, Values } from '@mode-switch/model';
import { PivotQuery } from '@mode-switch/query';
import { EMPTY_ENCODING, EMPTY_MARKS, EncField, Encoding, Guide, VariableType } from '@mode-vegas/vegas';
import { ChartFacade } from '@mode/charts/contract';
import {
  ChartFormatHelpers,
  EncodingHelpers,
  Flamingo,
  QueryBuilder,
  SwitchSchemaModel,
  TotalHelpers,
  makeFilters,
  safeMapValues,
} from '@mode/charts/util-vegas';
import {
  ChartTypes,
  DataProfilingTypes,
  DataViewTypes,
  FeatureFlag,
  FeatureFlagsFacade,
  FlamingoTypes,
} from '@mode/shared/contract-common';
import {
  DEFAULT_FLAMINGO_PAGE_SIZE,
  FLAMINGO_RESERVED_REFS,
  isPresent,
  makeVizDesignerPlanMetadataId,
} from '@mode/shared/util-js';
import { DomainQueryBuilder, Switch } from '@mode/shared/util-switch';
import { DataViewFacade, FlamingoActions, PlanMetadataActions, PlanMetadataFacade } from '@mode/switch-views/contract';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  combineLatestWith,
  debounceTime,
  filter,
  first,
  map,
  mergeMap,
  pairwise,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { ChartSwitchActions } from '../..';
import { DomainAppState } from '../charts-domain.types';
import { selectChart } from './chart.actions';

export function temporaryIsChart(chartType: ChartTypes.ChartType) {
  return !['bigValue', 'pivotTable', 'table'].includes(chartType);
}

const PAGING_DEBOUNCE_TIME = 150;

const adapter = new FlamingoAdapter();

@Injectable()
export class ChartPlanEffects {
  // Base selectedChart observable that the designer plan effects will be based off of
  private selectedChart$ = this.actions$.pipe(
    ofType(selectChart),
    startWith(null),
    pairwise(),
    filter(([prev, next]) => prev == null || (next != null && prev.token !== next.token)),
    map(([, next]) => next),
    filter(isPresent),
    switchMap((action) => this.chartFacade.createCompleteFlamingoParams(action.token)),
    switchMap(({ switchViewToken, chartType, chartToken, encoding, isAutoUpdateDisabled, manuallyApplyChanges }) => {
      return this.dataViewFacade.createDatasetBySwitchViewToken(switchViewToken).pipe(
        // This filter prevents this effect from running when no datasetResult is present, like in the non-gf context,
        // so we don't explicitly need the flag filter here. However, if we ever reduce this state behind the scenes as
        // we move forward with refactors, this will then run.
        filter(isPresent),
        filter((datasetResult) => {
          return [DataViewTypes.QueryRunStatus.Succeeded, DataViewTypes.QueryRunStatus.Pending].includes(
            datasetResult.queryRunState
          );
        }),
        switchMap((datasetResult) => {
          return this.dataViewFacade.attrUpdated$.pipe(
            startWith(null), // start with null so we render initially
            // Return datasetResult here, as we'll need queryToken and queryRunToken
            map(() => datasetResult)
          );
        }),
        withLatestFrom(this.dataViewFacade.model$, this.chartFacade.createChartFormatFromVizToken(chartToken)),
        map(([datasetResult, model, chartFormat]) => ({
          switchViewToken,
          model,
          datasetResult,
          chartFormat,
          chartToken,
          chartType,
          encoding,
          isAutoUpdateDisabled,
          manuallyApplyChanges,
        }))
      );
    })
  );

  // Viz Designer Chart plan effect
  designerChartPlan$ = createEffect(() =>
    this.selectedChart$.pipe(
      filter(
        ({ datasetResult, chartType, model, encoding, isAutoUpdateDisabled, manuallyApplyChanges }) =>
          chartType !== 'table' &&
          (!isAutoUpdateDisabled || manuallyApplyChanges) &&
          EncodingHelpers.validateChartEncodingFields(encoding, SwitchSchemaModel.make(model, datasetResult.queryToken))
            .length <= 0 &&
          EncodingHelpers.isChartEncodingComplete(encoding, chartType)
      ),
      tap(({ chartToken }) => this.planMetadataFacade.initPagingIfUnset(makeVizDesignerPlanMetadataId(chartToken))), // init the paging, but don't override existing value
      map(({ datasetResult, model, encoding, chartFormat, chartType, chartToken }) => {
        const needsColorBackfill = temporaryIsChart(chartType) && chartFormat?.colors.map == null;
        const includeSortDomain = ChartFormatHelpers.useMuzeRenderer(chartType, chartFormat);
        const plan = QueryBuilder.buildPivotQuery(
          adapter,
          model,
          datasetResult.queryToken,
          encoding,
          needsColorBackfill,
          includeSortDomain
        );
        return FlamingoActions.executePlan({
          refId: datasetResult.isMetric ? chartToken : FLAMINGO_RESERVED_REFS.ChartDesigner,
          queryRunToken: datasetResult.queryRunToken,
          allowRefresh: true,
          plan,
          vizToken: chartToken,
        });
      })
    )
  );

  // Viz Designer Table plan effect
  designerTablePlan$ = createEffect(() =>
    this.selectedChart$.pipe(
      filter(
        ({ datasetResult, chartType, model, encoding, isAutoUpdateDisabled, manuallyApplyChanges }) =>
          chartType === 'table' &&
          (!isAutoUpdateDisabled || manuallyApplyChanges) &&
          EncodingHelpers.validateTableEncodingFields(encoding, SwitchSchemaModel.make(model, datasetResult.queryToken))
            .length <= 0 &&
          EncodingHelpers.isChartEncodingComplete(encoding, chartType)
      ),
      tap(({ chartToken, chartFormat }) =>
        this.planMetadataFacade.initPagingIfUnset(
          makeVizDesignerPlanMetadataId(chartToken),
          0,
          chartFormat?.flatTable?.general?.pageSize
        )
      ), // init the paging, but don't override existing value
      switchMap((data) =>
        this.planMetadataFacade.createOffsetSelector(makeVizDesignerPlanMetadataId(data.chartToken)).pipe(
          first(),
          map((offset) => ({ ...data, offset }))
        )
      ),
      map(({ datasetResult, model, encoding, offset, chartToken, chartFormat }) => {
        const plan = QueryBuilder.buildRecordsPlan(
          adapter,
          model,
          datasetResult.queryToken,
          encoding,
          chartFormat?.flatTable?.general?.pageSize ?? DEFAULT_FLAMINGO_PAGE_SIZE,
          offset ?? 0
        );
        return FlamingoActions.executePlan({
          refId: datasetResult.isMetric ? chartToken : FLAMINGO_RESERVED_REFS.ChartDesigner,
          queryRunToken: datasetResult.queryRunToken,
          allowRefresh: true,
          plan,
          vizToken: chartToken,
        });
      })
    )
  );

  /**
   * Paging for tables in the designer.
   *
   * ie - refId = vizToken
   * This effect as written only handles the vizDesigner in the editor context.
   *
   * @TODO - As we move forward, we will have to decide how we expect to implement action
   * hygine here. Do we consider same component/dispatch location but different context to be a
   * different action? ie - exploration and explorer paging. If so, we will probably have multiple
   * similar effects that just handle different actions. Seems like two reasonable ways to move forward
   * with this pattern:
   * 1. We may be able to abstract the actual stream logic that will be repeated.
   * 2. Alternatively, we may want to try to use a generic effect that can handle all of the different
   *    actions, giving the actions a similar interfaces that can accommodate their differences.
   */
  setOffsetForTableInDesigner$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PlanMetadataActions.setOffsetInDesigner, PlanMetadataActions.setPageSizeInDesigner),
      debounceTime(PAGING_DEBOUNCE_TIME),
      switchMap((action) =>
        this.chartFacade.createChartTypeEncodingFromVizToken(action.vizToken).pipe(
          filter(isPresent),
          filter(({ chartType }) => chartType === 'table'),
          first(),
          switchMap((data) =>
            this.dataViewFacade.createQueryRunTokenByQueryToken(data.queryToken).pipe(
              filter(isPresent),
              map((runToken) => ({ ...data, runToken }))
            )
          ),
          withLatestFrom(
            this.dataViewFacade.model$,
            this.getDatasetResultByVizToken(action.vizToken),
            this.chartFacade.createChartFormatFromVizToken(action.vizToken)
          ),
          map(([chartData, model, datasetResult, chartFormat]) => {
            const pageSize = chartFormat?.flatTable?.general?.pageSize ?? DEFAULT_FLAMINGO_PAGE_SIZE;
            const plan = QueryBuilder.buildRecordsPlan(
              adapter,
              model,
              chartData.queryToken,
              chartData.encoding,
              pageSize,
              action.offset
            );
            return FlamingoActions.executePagePlan({
              refId: datasetResult?.isMetric ? chartData.chartToken : FLAMINGO_RESERVED_REFS.ChartDesigner,
              queryRunToken: chartData.runToken,
              allowRefresh: true,
              plan,
            });
          })
        )
      )
    );
  });

  requestByManualSortDomain$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ChartSwitchActions.viewCalcModalManualSortDomainRequest),
      switchMap((action) =>
        this.chartFacade.createQueryTokenFromVizToken(action.token).pipe(
          filter(isPresent),
          first(),
          switchMap((queryToken) =>
            this.dataViewFacade.createQueryRunTokenByQueryToken(queryToken).pipe(
              filter(isPresent),
              map((queryRunToken) => ({ queryToken, queryRunToken }))
            )
          ),
          withLatestFrom(this.dataViewFacade.model$),
          map(([data, model]) => {
            const { field, refId, items } = action;
            const { queryToken, queryRunToken } = data;
            const view = Switch.Views.find(model, queryToken);
            if (view) {
              const modeField = Switch.field(model, view.name, field.formula, Attr.Type.Discrete, field.id);
              if (modeField) {
                return FlamingoActions.executePlan({
                  plan: buildDomainPlan(model, view, action.encoding, modeField, items),
                  queryRunToken: queryRunToken,
                  allowRefresh: true,
                  refId,
                });
              }
            }
            return ChartSwitchActions.viewCalcModalManualSortDomainRequestFailure({
              field,
              view,
            });
          })
        )
      )
    );

    ///
    ///

    function buildDomainPlan(
      model: FlamingoTypes.ModeModel,
      view: FlamingoTypes.ModeView,
      encoding: Encoding,
      field: FlamingoTypes.ModeField,
      items: unknown[] = []
    ) {
      const fieldMap = new Map([[field.alias, field]]);
      const sortItems = safeMapValues(items, field.formula.type);
      const pivot = PivotQuery.Basic.make({
        filters: makeFilters(model, view.name, encoding, fieldMap),
        sorts: [Sort.ByManual.make(field, sortItems)],
      });
      return DomainQueryBuilder.buildPivotDomainQuery(
        adapter,
        Switch.Models.makeFromView(view.name, view),
        view.name,
        pivot,
        [
          {
            alias: field.alias,
            formula: field.formula.source,
            discrete: true,
          },
        ]
      );
    }
  });

  binSizePlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.getBinSize),
      switchMap((action) => {
        return this.chartFacade.createCompleteFlamingoParamsWithEmptyEncoding(action.token).pipe(
          map((result) => {
            return {
              ...result,
              columnName: action.columnName,
              token: action.token,
              isDesignerView: action.isDesignerView,
              refId: action.refId,
            };
          })
        );
      }),
      switchMap(({ columnName, token, isDesignerView, chartToken, encoding, switchViewToken, refId }) => {
        return this.dataViewFacade.createDatasetBySwitchViewToken(switchViewToken).pipe(
          // This filter prevents this effect from running when no datasetResult is present, like in the non-gf context,
          // so we don't explicitly need the flag filter here. However, if we ever reduce this state behind the scenes as
          // we move forward with refactors, this will then run.
          filter(isPresent),
          filter((datasetResult) => datasetResult.queryRunState === DataViewTypes.QueryRunStatus.Succeeded),
          switchMap((datasetResult) => {
            return this.dataViewFacade.attrUpdated$.pipe(
              startWith(null), // start with null so we render initially
              // Return datasetResult here, as we'll need queryToken and queryRunToken
              map(() => datasetResult)
            );
          }),
          withLatestFrom(this.dataViewFacade.model$),
          map(([datasetResult, model]) => ({
            model,
            datasetResult,
            chartToken,
            encoding,
            columnName,
            token,
            isDesignerView,
            refId,
          }))
        );
      }),
      map(
        ({
          model,
          datasetResult: { queryToken: viewName, queryRunToken },
          columnName: attrName,
          chartToken,
          isDesignerView,
          refId,
        }) => {
          const encoding: Encoding = {
            ...EMPTY_ENCODING,
            marks: {
              series: [],
              channel: '*',
              properties: {
                ...EMPTY_MARKS,
                text: {
                  values: [
                    {
                      id: 'binSize',
                      formula: `IFNULL(BINSIZE([${attrName}]), 1)`,
                      variable: {
                        type: VariableType.CONTINUOUS,
                      },
                    },
                    {
                      id: 'name',
                      formula: `'${attrName}'`,
                      variable: {
                        type: VariableType.DISCRETE,
                      },
                    },
                  ],
                },
              },
            },
          };

          let plan = QueryBuilder.buildPivotQuery(adapter, model, viewName, encoding);
          return FlamingoActions.executePlan({
            queryRunToken: queryRunToken,
            refId: refId
              ? refId
              : `${FLAMINGO_RESERVED_REFS.BinSize}#${
                  isDesignerView ? FLAMINGO_RESERVED_REFS.ChartDesigner : chartToken
                }`,
            plan,
            allowRefresh: true,
          });
        }
      )
    )
  );

  dataProfilingPlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.getDataProfiling),
      switchMap((action) => {
        return this.chartFacade.createCompleteFlamingoParams(action.token).pipe(
          map((result) => {
            return {
              ...result,
              cols: action.cols,
              reportFilters: action.reportFilters,
              isDesignerView: action.isDesignerView,
              refId: action.referenceId,
            };
          })
        );
      }),
      switchMap(({ cols, reportFilters, isDesignerView, chartToken, encoding, switchViewToken, refId }) => {
        return this.dataViewFacade.createDatasetBySwitchViewToken(switchViewToken).pipe(
          // This filter prevents this effect from running when no datasetResult is present, like in the non-gf context,
          // so we don't explicitly need the flag filter here. However, if we ever reduce this state behind the scenes as
          // we move forward with refactors, this will then run.
          filter(isPresent),
          filter((datasetResult) => datasetResult.queryRunState === DataViewTypes.QueryRunStatus.Succeeded),
          switchMap((datasetResult) => {
            return this.dataViewFacade.attrUpdated$.pipe(
              startWith(null), // start with null so we render initially
              // Return datasetResult here, as we'll need queryToken and queryRunToken
              map(() => datasetResult)
            );
          }),
          withLatestFrom(this.dataViewFacade.model$),
          map(([datasetResult, model]) => ({
            model,
            datasetResult,
            chartToken,
            encoding,
            cols,
            reportFilters,
            isDesignerView,
            refId,
          }))
        );
      }),
      map((result) => {
        type Plan = ReturnType<typeof QueryBuilder.buildPivotQuery>;
        const vName = result.datasetResult.queryToken;
        const model = result.model as FlamingoTypes.ModeModel;
        const view = Switch.Views.find(model, vName) as FlamingoTypes.ModeView;
        const allAttrs = Switch.Attrs.getAll(model, vName);
        const selectedAttrs: FlamingoTypes.ModeAttr[] = [];

        //filtering the attributes which are present in encoding
        result.cols.forEach((field: DataProfilingTypes.DataProfilingColInfo) => {
          const index = allAttrs.findIndex((d) => d.name === field.name);
          if (index >= 0) {
            selectedAttrs.push(allAttrs[index]);
          }
        });

        // No attrs, no plan
        if (!selectedAttrs.length) {
          return;
        }

        const plan = selectedAttrs.map((attr, index) => buildPlan(attr, index)).reduce(merge);
        return FlamingoActions.executePlan({
          queryRunToken: result.datasetResult.queryRunToken,
          allowRefresh: true,
          refId: `${result.refId}#${result.isDesignerView ? FLAMINGO_RESERVED_REFS.ChartDesigner : result.chartToken}`,
          plan,
          vizToken: result.chartToken,
        });

        ///
        ///

        function encf(id: string, formula: string): EncField {
          return {
            id,
            formula,
            variable: {
              type: VariableType.DISCRETE,
            },
          };
        }

        function dimension(attr: FlamingoTypes.ModeAttr) {
          switch (attr.formula.type.name) {
            case DataType.Name.Boolean:
              return [encf('count', `count( if [${attr.name}] = true  then 1 end )`)];
            case DataType.Name.String: {
              return [encf('countd', `countd([${attr.name}])`)];
            }
            case DataType.Name.Timestamp: {
              return [encf('min', `min([${attr.name}])`), encf('max', `max([${attr.name}])`)];
            }
            case DataType.Name.DateTime: {
              return [encf('min', `min([${attr.name}])`), encf('max', `max([${attr.name}])`)];
            }
            default: {
              return [encf('countd', `countd([${attr.name}])`)];
            }
          }
        }

        function measure(attr: FlamingoTypes.ModeAttr) {
          if (
            attr.formula.role?.name === DataRole.Name.DatePart ||
            attr.formula.role?.name === DataRole.Name.DateTrunc
          ) {
            return [encf('min', `min([${attr.name}])`), encf('max', `max([${attr.name}])`)];
          }
          return [
            encf('sum', `sum([${attr.name}])`),
            encf('min', `min([${attr.name}])`),
            encf('max', `max([${attr.name}])`),
            encf('avg', `avg([${attr.name}])`),
          ];
        }

        function buildEncoding(attr: FlamingoTypes.ModeAttr, index: number): Encoding {
          let values: EncField[] = [];
          let type: string;

          if (Switch.Attrs.detectDimension(attr.name, attr.formula.type.name)) {
            values = dimension(attr);
            type = 'dimension';
          } else {
            values = measure(attr);
            type = 'measure';
          }
          values = values.concat([encf('name', `'${result.cols[index].field}'`)]);
          values = values.concat([encf('type', `'${type}'`)]);

          let newEncoding = result.encoding;

          if (result.reportFilters) {
            const validFilters = Flamingo.getValidReportFilters(
              model,
              result.datasetResult.queryToken,
              result.reportFilters
            );
            newEncoding = EncodingHelpers.addReportFiltersToChartEncoding(view, newEncoding, validFilters, false);
          }

          return {
            ...EMPTY_ENCODING,
            filter: newEncoding.filter,
            sorts: [],
            marks: {
              series: [],
              channel: '*',
              properties: {
                ...EMPTY_MARKS,
                text: {
                  values,
                },
              },
            },
          };
        }

        function buildPlan(attr: FlamingoTypes.ModeAttr, index: number) {
          return QueryBuilder.buildPivotQuery(adapter, model, view.name, buildEncoding(attr, index));
        }

        function merge(plan: Plan, next: Plan): Plan {
          return {
            ...plan,
            id: result.chartToken,
            queries: plan.queries.concat(next.queries),
          };
        }
      }),
      filter(isPresent)
    )
  );

  fetchFieldDomainValues$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ChartSwitchActions.filterGetDomainValuesRequest, ChartSwitchActions.colorsGetDomainValuesRequest),
      map((action) => {
        const domainAlias = action.domainAlias;
        const view = action.view;
        const model = Switch.Models.makeFromView(view.name, view);
        let plan: FlamingoTypes.ModeSelectPlan;
        const encoding = EncodingHelpers.makeFieldDomainQueryEncoding(
          action.encoding,
          action.field,
          action.withFilters
        );
        const pivotPlan = QueryBuilder.buildPivotQuery(adapter, model, view.name, encoding);
        const summary = QueryBuilder.summarizePivotPlan(pivotPlan);

        const summaryQuery = summary.queries.find((q) => q.series === action.series) ?? summary.queries[0];
        if (typeof summaryQuery === 'undefined') {
          throw new Error(`Plan summary must include at least 1 query`);
        }

        plan = DomainQueryBuilder.buildPivotDomainQuery(adapter, model, view.name, summaryQuery.query, [
          {
            alias: domainAlias,
            formula: action.field.formula,
            discrete: action.field.isDiscrete,
            frame: action.field.frame,
          },
        ]);
        return FlamingoActions.executePlan({
          plan,
          queryRunToken: action.queryRunToken,
          allowRefresh: true,
          refId: action.refId,
        });
      })
    );
  });

  /**
   * Executes a plan which gathers the raw data required to calculate the min/max
   * domain of a continuous field in a mark channel across one or more series.
   *
   * The actual calculation of the min/max domain is the responsibility of the
   * code which handles the response.
   */
  fetchContinuousMarkDomainRange$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ChartSwitchActions.continuousMarkDomainRangeRequest),
      map((action) => {
        const { queryRunToken, view, encoding, refId, alias, channel } = action;

        // The current domain plans do not handle unioning ranges for a mark
        // channel field across multiple series. Each series can have a
        // different level of detail and that is not handled in the domain plan.
        //
        // We must leave the field in the current position in the encoding
        // because analytic calcs require the proper partitioning and addressing.
        //
        // So we will execute a plan which the existing encoding minus a few
        // unneeded aspects.
        const encField = EncodingHelpers.getEncFieldById(encoding, alias);
        if (encField === undefined) {
          throw Error(`Unable to locate field: ${alias}`);
        }
        const formula = encField.formula;

        // Special handling for value fields. We match $VALUES on the
        // channel instead of the passed formula.
        const valueField = encoding.value.find((v) => v.id === alias);
        const channelFormula = valueField != null ? Values.Key : formula;

        // Remove sorts and totals from the encoding
        let queryEncoding = EncodingHelpers.clearSorts(encoding).encoding;
        queryEncoding = TotalHelpers.disableColGrandTotal(queryEncoding).encoding;
        queryEncoding = TotalHelpers.disableRowGrandTotal(queryEncoding).encoding;

        // Remove any series that does not have the expected formula
        // on the specified channel
        const unneededSeries = queryEncoding.marks.series.filter((series) => {
          return series.properties[channel].values[0]?.formula !== channelFormula;
        });

        if (unneededSeries.length > 0) {
          queryEncoding = EncodingHelpers.removeFieldsFromEncoding(
            queryEncoding,
            unneededSeries.map((s) => s.field)
          ).encoding;
        }

        // If the field is in Measure Values and not directly in the channel,
        // then we replace Measure Values with the field in the channel.
        // By using the same id as MV, we can later retrieve the correct
        // value by using the id of the field in the channel in the original
        // encoding.
        if (valueField?.id != null) {
          const encoder = new Guide().newBatchOperation(queryEncoding);

          // Remove the field from value
          encoder.removeField(valueField.id);

          // Make a map of series field to Measure Values field id
          // This is useful for the later batch operations.
          const seriesMap =
            queryEncoding.marks.channel === '*'
              ? new Map([['*', queryEncoding.marks.properties[channel].values[0]]])
              : new Map(
                  queryEncoding.marks.series.map((series) => {
                    return [series.field, series.properties[channel].values[0]];
                  })
                );

          // Remove the actual Measure Values fields from all of the series.
          // The 'values' in the map are the Measure Values fields in the channel
          // across the series.
          const mvFieldIds = Array.from(seriesMap.values())
            .map((f) => f.id)
            .filter((f): f is string => f !== undefined);

          encoder.removeFields(mvFieldIds);

          // Add the value field to each series using the same id as
          // Measure Values had (when we removed it above).
          Array.from(seriesMap.entries()).forEach(([seriesId, field]) => {
            encoder.addEncFieldToSeries({ ...valueField, id: field.id ?? '' }, channel, seriesId);
          });

          queryEncoding = encoder.complete().encoding;
        }

        const model = Switch.Models.makeFromView(view.name, view);
        const plan = QueryBuilder.buildPivotQuery(adapter, model, view.name, queryEncoding);
        return FlamingoActions.executePlan({
          plan,
          refId,
          queryRunToken,
          allowRefresh: true,
        });
      })
    );
  });

  private getDatasetResultByVizToken(vizToken: string) {
    return this.chartFacade.createSwitchViewTokenFromVizToken(vizToken).pipe(
      filter(isPresent),
      mergeMap((switchViewToken) => {
        return this.dataViewFacade.createDatasetBySwitchViewToken(switchViewToken);
      })
    );
  }

  constructor(
    private actions$: Actions,
    private store: Store<DomainAppState>,
    private chartFacade: ChartFacade,
    private dataViewFacade: DataViewFacade,
    private planMetadataFacade: PlanMetadataFacade,
    private featureFlagsFacade: FeatureFlagsFacade
  ) {}
}
